import { createTheme } from "@mui/material";
import { getTelegramThemeParams, isTelegramWebApp } from "./telegramUtils";

export const createGlobalTheme = () => {
    const tp = getTelegramThemeParams();
    const normalize = (c, fb) => {
        if (!c || typeof c !== 'string') return fb;
        const s = c.trim();
        if (!s) return fb;
        if (s.startsWith('#') || s.startsWith('rgb')) return s;
        if (/^[0-9a-fA-F]{6,8}$/.test(s)) return `#${s}`;
        return s;
    };
    const pick = (obj, keys) => {
        if (!obj) return undefined;
        for (const k of keys) {
            if (obj[k] != null) return obj[k];
        }
        return undefined;
    };
    const overrideTheme = (() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const q = (params.get('tg_test_theme') || params.get('tg_light') || '').toLowerCase();
            if (q && (q === 'light' || q === '1' || q === 'true')) return 'light';
            const ls = (window.localStorage.getItem('tg_test_theme') || '').toLowerCase();
            if (ls === 'light') return 'light';
        } catch { }
        return null;
    })();
    const mode = (() => {
        if (overrideTheme === 'light') return 'light';
        if (isTelegramWebApp()) return tp?.colorScheme === 'light' ? 'light' : 'dark';
        return 'light';
    })();

    const fb = mode === 'light'
        ? {
            backgroundDefault: '#FFFFFF',
            backgroundPaper: '#F5F8FC',
            textPrimary: '#232B48',
            textSecondary: '#5B5B5C',
            primaryMain: '#054CCC',
            primaryContrastText: '#FFFFFF',
        }
        : {
            backgroundDefault: '#14171D',
            backgroundPaper: '#292B30',
            textPrimary: '#F7FEFF',
            textSecondary: '#CED0D6',
            primaryMain: '#2D58EF',
            primaryContrastText: undefined,
        };

    const backgroundDefault = normalize(pick(tp, ['bg_color', 'backgroundColor']), fb.backgroundDefault);
    const backgroundPaper = normalize(pick(tp, ['secondary_bg_color', 'secondaryBackgroundColor']), fb.backgroundPaper);
    const textPrimary = normalize(pick(tp, ['text_color', 'textColor']), fb.textPrimary);
    const textSecondary = normalize(pick(tp, ['hint_color', 'hintColor']), fb.textSecondary);
    const primaryMain = normalize(pick(tp, ['button_color', 'buttonColor']), fb.primaryMain);
    const primaryContrastText = normalize(pick(tp, ['button_text_color', 'buttonTextColor']), fb.primaryContrastText || textPrimary);

    return createTheme({
        palette: {
            mode,
            background: {
                default: backgroundDefault,
                paper: backgroundPaper,
            },
            text: {
                primary: textPrimary,
                secondary: textSecondary,
            },
            primary: {
                main: primaryMain,
                contrastText: primaryContrastText,
            },
            secondary: {
                main: '#FFFFFF',
                contrastText: '#0F56D6',
            }
        },
        typography: {
            body1: {
                fontSize: '20px'
            },
            subtitle1: {
                fontSize: '16px',
                color: mode === 'light' ? '#232B48' : '#F2F3F5'
            },
            h1: {
                fontSize: '80px'
            },
            h2: {
                fontSize: '48px'
            },
            h5: {
                fontSize: '24px'
            },
            caption: {
                fontSize: '12px'
            },
            fontFamily: "Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
            fontWeight: 400
        },
        components: {
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: '12px',
                        '& legend': {
                            // marginLeft: '20px'
                        },
                        '& fieldset': {
                            borderColor: textPrimary,
                        },
                        '&:hover fieldset': {
                            borderColor: textPrimary,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: textPrimary,
                        },
                    }
                }
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        padding: '32px'
                    }
                }
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        color: textPrimary
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        color: textPrimary
                    }
                }
            },
            MuiButton: {
                defaultProps: {
                    disableRipple: true,
                    disableElevation: true,
                },
                styleOverrides: {
                    root: {
                        fontFamily: "'Roboto', sans-serif",
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '127%',
                        textTransform: 'none',
                        borderRadius: '6px',
                        padding: '14px 16px',
                        gap: '12px',
                        height: '48px',
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: 'none',
                        },
                        '&:active': {
                            boxShadow: 'none',
                        },
                    },
                    text: isTelegramWebApp() ? {
                        padding: 0,
                        minWidth: 0,
                        height: '25px',
                        borderRadius: 0,
                        gap: '4px',
                        color: primaryMain,
                        '&:hover': {
                            backgroundColor: 'transparent',
                            color: primaryMain,
                        },
                        '&:active': {
                            backgroundColor: 'transparent',
                            color: primaryMain,
                        },
                        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                            margin: 0,
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: '16px',
                            color: 'currentColor',
                        },
                    } : {
                        padding: 0,
                        minWidth: 0,
                        height: '25px',
                        borderRadius: 0,
                        gap: '4px',
                        color: '#054CCC',
                        '&:hover': {
                            backgroundColor: 'transparent',
                            color: '#0038B8',
                        },
                        '&:active': {
                            backgroundColor: 'transparent',
                            color: '#0024A4',
                        },
                        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                            margin: 0,
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: '16px',
                            color: 'currentColor',
                        },
                    },
                    // Синяя кнопка (Blue variant - primary, по умолчанию)
                    contained: isTelegramWebApp() ? {
                        backgroundColor: primaryMain,
                        color: primaryContrastText,
                        border: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: primaryMain,
                            boxShadow: 'none',
                        },
                        '&:active': {
                            backgroundColor: primaryMain,
                            boxShadow: 'none',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#CCCCCC',
                            color: '#FFFFFF',
                        },
                    } : {
                        backgroundColor: '#054CCC',
                        color: '#FFFFFF',
                        border: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#0038B8',
                            boxShadow: 'none',
                        },
                        '&:active': {
                            backgroundColor: '#0024A4',
                            boxShadow: 'none',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#CCCCCC',
                            color: '#FFFFFF',
                        },
                    },
                    // Белая кнопка (White variant - secondary)
                    containedSecondary: {
                        backgroundColor: '#FFFFFF',
                        color: '#0F56D6',
                        border: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#EDF3FF',
                            color: '#0F56D6',
                            boxShadow: 'none',
                        },
                        '&:active': {
                            backgroundColor: '#EDF3FF',
                            color: '#0F56D6',
                            boxShadow: 'none',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#CCCCCC',
                            color: '#FFFFFF',
                        },
                        '&.MuiButton-colorError': {
                            backgroundColor: '#FF4D4F',
                        },
                    },
                    outlined: {
                        borderColor: textPrimary,
                    },
                }
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                        backgroundColor: textPrimary
                    }
                }
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiInputBase-root': {
                            borderRadius: '8px',
                            '& legend': {
                                // marginLeft: '20px'
                            },
                            '& fieldset': {
                                borderColor: textPrimary,
                            },
                            '&:hover fieldset': {
                                borderColor: textPrimary,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: textPrimary,
                            },
                        }
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',

                        '&>.MuiSvgIcon-root': {
                            color: textPrimary
                        },
                        '& legend': {
                            // marginLeft: '20px'
                        },
                        '& fieldset': {
                            borderColor: textPrimary,
                        },
                        '&:hover fieldset': {
                            borderColor: textPrimary,
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: textPrimary,
                        },
                    }
                }
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: textPrimary,
                        // marginLeft: '20px',
                        '&.Mui-focused': {
                            color: textPrimary,
                        },
                    }
                }
            },
            MuiInputAdornment: {
                styleOverrides: {
                    root: {
                        color: textPrimary
                    }
                }
            },
            MuiDateCalendar: {
                styleOverrides: {
                    root: {
                        backgroundColor: backgroundPaper,
                        color: textPrimary,
                        borderRadius: '8px'
                    }
                }
            },
            MuiPickersCalendarHeader: {
                styleOverrides: {
                    root: {
                        color: textPrimary
                    },
                    label: {
                        color: textPrimary
                    },
                    switchViewIcon: {
                        color: textPrimary
                    },
                    switchViewButton: {
                        color: textPrimary
                    },
                    chevronLeft: { color: textPrimary },
                    chevronRight: { color: textPrimary }
                }
            },
            MuiPickersDay: {
                styleOverrides: {
                    root: {
                        color: textPrimary,
                        '&.Mui-selected': {
                            backgroundColor: primaryMain,
                            color: textPrimary,
                        },
                        '&.MuiPickersDay-today:not(.Mui-selected)': {
                            borderColor: primaryMain
                        }
                    }
                }
            },
            MuiPickersLayout: {
                styleOverrides: {
                    root: {
                        backgroundColor: backgroundPaper,
                        color: textPrimary,
                        borderRadius: '8px'
                    },
                    contentWrapper: {
                        backgroundColor: backgroundPaper
                    },
                }
            },
            MuiPickersToolbar: {
                styleOverrides: {
                    root: {
                        backgroundColor: backgroundPaper,
                        color: textPrimary
                    }
                }
            },
            MuiClock: {
                styleOverrides: {
                    root: {
                        backgroundColor: backgroundPaper
                    }
                }
            },
            MuiClockNumber: {
                styleOverrides: {
                    root: {
                        color: textPrimary
                    },
                }
            },
            MuiTimeClock: {
                styleOverrides: {
                    root: {
                        backgroundColor: backgroundPaper,
                        color: textPrimary
                    }
                }
            },
            MuiPickersTextField: {
                styleOverrides: {
                    root: {
                        borderRadius: '12px',
                        '& fieldset': { borderColor: textPrimary },
                        '&:hover fieldset': { borderColor: textPrimary },
                        '&.Mui-focused fieldset': { borderColor: textPrimary },
                    }
                }
            },
            MuiCssBaseline: {
                styleOverrides: {
                    '.MuiPickersInputBase-root.MuiPickersOutlinedInput-root': {
                        borderRadius: '12px',
                        color: textPrimary
                    },
                    '.MuiPickersOutlinedInput-root legend': {
                        // marginLeft: '20px'
                    },
                    '.MuiPickersOutlinedInput-root fieldset': {
                        borderColor: textPrimary
                    },
                    '.MuiPickersOutlinedInput-root:hover fieldset': {
                        borderColor: textPrimary
                    },
                    '.MuiPickersOutlinedInput-root.Mui-focused fieldset': {
                        borderColor: textPrimary
                    },
                    '.MuiPickersInputBase-colorPrimary': {
                        color: textPrimary
                    },
                    '.MuiPickersInputBase-inputSizeSmall': {
                        paddingTop: '10px',
                        paddingBottom: '10px'
                    },
                    '.MuiPickersInputBase-adornedEnd': {
                        color: textPrimary
                    },
                    'input[type=number]': { MozAppearance: 'textfield', appearance: 'textfield' },
                    'input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                    'input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
                }
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        // color: '#F7FEFF',
                        backgroundColor: backgroundPaper,
                        fontSize: '16px'
                    }
                }
            },
            MuiSwitch: {
                styleOverrides: {
                    root: {
                        width: '40px !important',
                        minWidth: '40px !important',
                        height: '20px !important',
                        padding: '0 !important',
                        flexShrink: 0,
                        mt: '2px',
                    },
                    switchBase: {
                        padding: '3px !important',
                        '&.Mui-checked': {
                            transform: 'translateX(20px) !important',
                            '& + .MuiSwitch-track': {
                                backgroundColor: `${primaryMain} !important`,
                                opacity: 1,
                            },
                            '& .MuiSwitch-thumb': {
                                backgroundColor: '#FFFFFF !important',
                            },
                        },
                    },
                    thumb: {
                        width: '14px !important',
                        height: '14px !important',
                        backgroundColor: '#FFFFFF !important',
                        boxShadow: 'none !important',
                    },
                    track: {
                        borderRadius: '100px !important',
                        backgroundColor: '#E7E8EB !important',
                        opacity: 1,
                    },
                }
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        boxSizing: 'border-box',
                        background: backgroundDefault,
                        border: '1px solid #EAEFFE',
                        borderRadius: '12px',
                        boxShadow: 'none',
                    },
                    list: {
                        padding: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: '8px',
                        gap: '4px',
                        minHeight: '36px',
                        borderRadius: '6px',
                        alignSelf: 'stretch',
                        fontFamily: "'Roboto', sans-serif",
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '127%',
                        color: textPrimary,
                        '&:hover': {
                            backgroundColor: '#EAEFFE',
                        },
                        '&.Mui-selected': {
                            backgroundColor: '#F4F6FE',
                            '&:hover': {
                                backgroundColor: '#EAEFFE',
                            },
                        },
                    },
                },
            },
            MuiList: {
                styleOverrides: {
                    root: {
                        padding: '6px',
                    },
                },
            },
        }
    });
};
